import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, MessageSquare, Loader } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useForumStats } from '../hooks/useApi';
import { uploadImageToImgBB } from '../utils/imgbb';
import ForumSidebar from './forum/ForumSidebar';
import ForumHeroBar from './forum/ForumHeroBar';
import ForumSearchBar from './forum/ForumSearchBar';
import ForumThreadList from './forum/ForumThreadList';
import ForumThreadDetail from './forum/ForumThreadDetail';
import ForumNewTopicModal from './forum/ForumNewTopicModal';
import ForumConfirmDialog from './forum/ForumConfirmDialog';
import { categoryLabels, ForumCategory, ForumThreadRow } from './forum/forumLabels';

const ForumPage: React.FC = () => {
  const { threadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoggedIn, openAuthModal } = useAuth();
  const { showToast } = useToast();
  const { stats, loading: statsLoading } = useForumStats();

  const [isMobile, setIsMobile] = useState(false);
  const [category, setCategory] = useState<ForumCategory | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [threads, setThreads] = useState<ForumThreadRow[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listHasMore, setListHasMore] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [thread, setThread] = useState<ForumThreadRow | null>(null);
  const [replies, setReplies] = useState<any[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState<ForumCategory>('genel');
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [replyContent, setReplyContent] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const c = () => setIsMobile(window.innerWidth < 900);
    c();
    window.addEventListener('resize', c);
    return () => window.removeEventListener('resize', c);
  }, []);

  useEffect(() => {
    const q = searchParams.get('category');
    if (q === 'genel' || q === 'not_istek' || q === 'soru') setCategory(q);
  }, [searchParams]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const fetchList = useCallback(
    async (pageNum: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoadingList(true);
      try {
        const res = await api.getForumThreads({
          page: pageNum,
          limit: 15,
          category: category || undefined,
        });
        if (res.success) {
          const next = res.data.threads || [];
          const pag = res.data.pagination;
          if (append) setThreads((prev) => [...prev, ...next]);
          else setThreads(next);
          setListHasMore(Boolean(pag && pag.current < pag.pages));
          setListPage(pageNum);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingList(false);
        setLoadingMore(false);
      }
    },
    [category]
  );

  useEffect(() => {
    if (threadId) return;
    setListPage(1);
    fetchList(1, false);
  }, [threadId, category, fetchList]);

  const fetchThread = useCallback(async (id: string) => {
    setLoadingThread(true);
    try {
      const res = await api.getForumThread(id);
      if (res.success) {
        setThread(res.data.thread);
        setReplies(res.data.replies || []);
      }
    } catch (e) {
      console.error(e);
      setThread(null);
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    if (threadId) fetchThread(threadId);
    else {
      setThread(null);
      setReplies([]);
    }
  }, [threadId, fetchThread]);

  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(
      (t) => t.title.toLowerCase().includes(q) || t.content.toLowerCase().includes(q)
    );
  }, [threads, searchQuery]);

  const goHome = () => navigate('/');
  const openThread = (id: string) => navigate(`/forum/thread/${id}`);
  const backToList = () => navigate('/forum');

  const loadMoreThreads = () => {
    if (!listHasMore || loadingList || loadingMore) return;
    fetchList(listPage + 1, true);
  };

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const f of files.slice(0, 6)) {
      if (!f.type.startsWith('image/')) continue;
      urls.push(await uploadImageToImgBB(f));
    }
    return urls;
  };

  const handleNewTopicClick = () => {
    if (!isLoggedIn) openAuthModal('login');
    else setShowNew(true);
  };

  const handleCreateThread = async () => {
    if (!isLoggedIn) {
      openAuthModal('login');
      return;
    }
    if (newTitle.trim().length < 3 || !newContent.trim()) return;
    setSubmitting(true);
    try {
      let imageUrls: string[] = [];
      if (newFiles.length) imageUrls = await uploadFiles(newFiles);
      await api.createForumThread({
        title: newTitle.trim(),
        content: newContent.trim(),
        category: newCategory,
        imageUrls,
      });
      setShowNew(false);
      setNewTitle('');
      setNewContent('');
      setNewFiles([]);
      fetchList(1, false);
    } catch (err: any) {
      showToast(err.message || 'Konu oluşturulamadı', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!threadId || !isLoggedIn) {
      openAuthModal('login');
      return;
    }
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      let imageUrls: string[] = [];
      if (replyFiles.length) imageUrls = await uploadFiles(replyFiles);
      await api.createForumReply(threadId, { content: replyContent.trim(), imageUrls });
      setReplyContent('');
      setReplyFiles([]);
      fetchThread(threadId);
      fetchList(1, false);
    } catch (err: any) {
      showToast(err.message || 'Yanıt gönderilemedi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteThread = async () => {
    if (!threadId || !thread) return;
    setDeleting(true);
    try {
      await api.deleteForumThread(threadId);
      setShowDeleteConfirm(false);
      backToList();
      fetchList(1, false);
    } catch (e: any) {
      showToast(e.message || 'Silinemedi', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const threadAuthor = thread ? (thread as any).author : null;
  const threadAuthorId =
    threadAuthor && typeof threadAuthor === 'object'
      ? String(threadAuthor._id)
      : threadAuthor
        ? String(threadAuthor)
        : '';
  const canDeleteThread = Boolean(
    thread && user && (threadAuthorId === String(user.id) || user.role === 'admin')
  );

  const handleCategoryChange = (c: ForumCategory | '') => {
    setCategory(c);
    if (threadId) backToList();
  };

  return (
    <div className="forum-layout">
      <ForumSidebar
        sidebarOpen={sidebarOpen}
        isMobile={isMobile}
        category={category}
        onGoHome={goHome}
        onBackToList={backToList}
        onCategoryChange={handleCategoryChange}
        onNewTopic={handleNewTopicClick}
      />

      {isMobile && sidebarOpen && (
        <button
          type="button"
          aria-label="Menüyü kapat"
          className="forum-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="forum-main">
        <header className="forum-main-header">
          {isMobile && (
            <button type="button" className="forum-header-btn" onClick={() => setSidebarOpen((o) => !o)}>
              <MessageSquare size={18} />
            </button>
          )}
          {!isMobile && (
            <button type="button" className="forum-header-btn forum-header-btn--text" onClick={() => setSidebarOpen((o) => !o)}>
              Menü
            </button>
          )}
          {threadId ? (
            <button type="button" onClick={backToList} className="forum-nav-btn forum-nav-btn--inline">
              <ChevronLeft size={18} />
              <span>Listeye dön</span>
            </button>
          ) : (
            <span className="forum-main-header__title">Konular</span>
          )}
        </header>

        <main className="forum-main-content">
          {threadId ? (
            loadingThread ? (
              <div className="forum-loading">
                <Loader size={32} color="var(--accent-primary)" />
              </div>
            ) : !thread ? (
              <div className="forum-empty-state glow-card">
                <p>Konu bulunamadı.</p>
                <button type="button" className="forum-btn forum-btn--ghost" onClick={backToList}>
                  Listeye dön
                </button>
              </div>
            ) : (
              <ForumThreadDetail
                thread={thread}
                replies={replies}
                loading={false}
                isLoggedIn={isLoggedIn}
                canDelete={canDeleteThread}
                submitting={submitting}
                replyContent={replyContent}
                replyFiles={replyFiles}
                onReplyContentChange={setReplyContent}
                onReplyFilesChange={setReplyFiles}
                onReply={handleReply}
                onDelete={() => setShowDeleteConfirm(true)}
              />
            )
          ) : (
            <>
              <ForumHeroBar
                totalThreads={stats?.totalThreads}
                totalReplies={stats?.totalReplies}
                loading={statsLoading}
              />
              <ForumSearchBar value={searchQuery} onChange={setSearchQuery} />
              <ForumThreadList
                threads={filteredThreads}
                loading={loadingList}
                loadingMore={loadingMore}
                listHasMore={listHasMore && !searchQuery.trim()}
                searchQuery={searchQuery}
                categoryLabel={category ? categoryLabels[category] : undefined}
                onOpenThread={openThread}
                onLoadMore={loadMoreThreads}
                onNewTopic={handleNewTopicClick}
              />
            </>
          )}
        </main>
      </div>

      <ForumNewTopicModal
        open={showNew}
        submitting={submitting}
        title={newTitle}
        content={newContent}
        category={newCategory}
        files={newFiles}
        onClose={() => setShowNew(false)}
        onTitleChange={setNewTitle}
        onContentChange={setNewContent}
        onCategoryChange={setNewCategory}
        onFilesChange={setNewFiles}
        onSubmit={handleCreateThread}
      />

      <ForumConfirmDialog
        open={showDeleteConfirm}
        message="Bu konuyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        onConfirm={handleDeleteThread}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleting}
      />
    </div>
  );
};

export default ForumPage;
